<template>
  <div class="space-y-4 text-brand-text dark:text-white/80">
    <div v-for="(entry, index) in scores" :key="index" class="flex items-center gap-2">
      <div class="whitespace-nowrap">Modify score</div>
      <IntegrationAutoComplete
        v-model="entry.score"
        :multiple="false"
        :options="availableScores"
        placeholder="s_house_hufflepuff"
        :arbitrary="true"
      />
      <div>by</div>
      <InputNumber
        v-model="entry.amount"
        inputId="horizontal-buttons"
        showButtons
        buttonLayout="horizontal"
        :step="1"
        mode="decimal"
      >
        <template #incrementicon>
          <span class="pi pi-plus" />
        </template>
        <template #decrementicon>
          <span class="pi pi-minus" />
        </template>
      </InputNumber>
      <Button
        v-if="scores.length > 1"
        icon="pi pi-trash"
        text
        severity="danger"
        size="small"
        @click="removeScore(index)"
      />
    </div>

    <div
      class="flex cursor-pointer items-center gap-2 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
      @click="addScore"
    >
      <span class="pi pi-plus text-xs" />
      Add another score
    </div>

    <div v-if="activeScores.length > 0" class="rounded-lg bg-surface-100 dark:bg-surface-800 p-4 text-sm space-y-1">
      <div v-for="(s, i) in activeScores" :key="i">
        When this answer is selected,
        <strong class="font-semibold">{{ s.amount > 0 ? 'increase' : 'decrease' }}</strong>
        the <strong class="font-semibold">{{ s.score }}</strong> score by
        <strong class="font-semibold">{{ Math.abs(s.amount) }}</strong>
      </div>
    </div>

    <div v-if="activeScores.length === 0" class="text-sm text-gray-500 dark:text-white/60">
      Enter a score dimension and adjust the value to enable scoring for this answer option.
    </div>
  </div>
</template>

<script setup lang="ts">
import { QuestionOption } from '@/types/config'
import { scoresInFlow } from '@rightmessage/flow-builder'

const props = defineProps<{
  option: QuestionOption
}>()

const route = useRoute()
const flowStore = useFlowStore()
const questionStore = useQuestionStore()

// Initialize scores array from the option
const scores = computed({
  get: () => {
    if (!props.option.scores || props.option.scores.length === 0) {
      // Ensure at least one empty row
      props.option.scores = [{ score: '', amount: 0 }]
    }
    return props.option.scores
  },
  set: (value) => {
    props.option.scores = value
  }
})

const activeScores = computed(() =>
  (props.option.scores || []).filter(s => s.score && s.amount !== 0)
)

const addScore = () => {
  if (!props.option.scores) props.option.scores = []
  props.option.scores.push({ score: '', amount: 0 })
}

const removeScore = (index: number) => {
  props.option.scores.splice(index, 1)
}

// Get available scores from the flow
const availableScores = () => {
  const offerFunnelId = route.params.offerFunnelId
    ? (Array.isArray(route.params.offerFunnelId)
        ? route.params.offerFunnelId[0]
        : route.params.offerFunnelId)
    : null

  if (!offerFunnelId) {
    return []
  }

  const flow = flowStore.getById(offerFunnelId)
  if (!flow) return []

  return scoresInFlow(flow.funnelDefinition, questionStore.questions).map((score) => {
    return {
      label: score,
      value: score
    }
  })
}
</script>
